import type { CodeSessionPullRequest } from '@/lib/code-sessions/pull-request';
import type { PrV2ReviewDecision } from '@/lib/pr-v2/reviewers';
import type { LucideIcon } from 'lucide-react';
import {
  CircleCheck,
  GitMerge,
  GitMergeConflict,
  GitPullRequest,
  GitPullRequestClosed,
  GitPullRequestDraft,
  X,
} from 'lucide-react';

export type SessionPrStatePresentation = {
  icon: LucideIcon;
  /** GitHub's state colors in /code's -400 shades (as diff +/- counts): green open, purple merged, red closed, grey draft. */
  className: string;
  label: string;
  /** The state alone, for rows that already read as a pull request. */
  shortLabel: string;
};

const SESSION_PR_STATE_PRESENTATION = {
  open: {
    icon: GitPullRequest,
    className: 'text-emerald-400',
    label: 'Open pull request',
    shortLabel: 'Open',
  },
  draft: {
    icon: GitPullRequestDraft,
    className: 'text-zinc-400',
    label: 'Draft pull request',
    shortLabel: 'Draft',
  },
  merged: {
    icon: GitMerge,
    className: 'text-purple-400',
    label: 'Merged pull request',
    shortLabel: 'Merged',
  },
  closed: {
    icon: GitPullRequestClosed,
    className: 'text-red-400',
    label: 'Closed pull request',
    shortLabel: 'Closed',
  },
} as const satisfies Record<CodeSessionPullRequest['state'], SessionPrStatePresentation>;

/** An open or draft pull request that conflicts with its base: amber, as it waits on someone. */
const SESSION_PR_CONFLICT_PRESENTATION = {
  icon: GitMergeConflict,
  className: 'text-amber-400',
  label: 'Pull request with merge conflicts',
  shortLabel: 'Conflicts',
} as const satisfies SessionPrStatePresentation;

/**
 * Where reviews leave an open pull request, in GitHub's review colors and the glyphs the rest of
 * cubic uses for them (reviewer list, merge menu, command palette): a green check for approved, a
 * red X for changes requested.
 */
const SESSION_PR_REVIEW_PRESENTATION = {
  approved: {
    icon: CircleCheck,
    className: 'text-emerald-400',
    label: 'Approved pull request',
    shortLabel: 'Approved',
  },
  changes_requested: {
    icon: X,
    className: 'text-red-400',
    label: 'Pull request with changes requested',
    shortLabel: 'Changes requested',
  },
} as const satisfies Record<PrV2ReviewDecision, SessionPrStatePresentation>;

/**
 * How every session surface draws a pull request: by its state, except that merge conflicts (which
 * only an open or draft one has) win, since they block it until someone resolves them. An open one
 * then shows its review decision; a draft stays a draft, as it is not ready for one.
 */
export function sessionPrStatePresentation(
  pullRequest: Pick<CodeSessionPullRequest, 'state' | 'hasConflicts' | 'reviewDecision'>,
): SessionPrStatePresentation {
  if (pullRequest.hasConflicts) {
    return SESSION_PR_CONFLICT_PRESENTATION;
  }
  if (pullRequest.state === 'open' && pullRequest.reviewDecision) {
    return SESSION_PR_REVIEW_PRESENTATION[pullRequest.reviewDecision];
  }
  return SESSION_PR_STATE_PRESENTATION[pullRequest.state];
}
